import { computed, defineComponent, ExtractPropTypes } from 'vue';
import { createNamespaceBem, makeCustomProp, makeStringProp, makeArrayProp } from '@/utils';
import {
  Field,
  Popup,
  Picker,
  DatePicker,
  type FieldProps as VanFieldProps,
  type PickerProps as VanPickerProps,
  type PickerOption as VanPickerOption,
} from 'vant';
import { t } from '@/assets/theme';
import { AreaCodeNode } from '@tencent/fd-types/patient';
import { Nullable } from '@/types/global';

const { name } = createNamespaceBem('field-picker');

export enum PickerTypeEnum {
  normal = 'normal',
  date = 'date',
  address = 'address',
}

const fieldPickerProps = {
  name: makeStringProp<VanFieldProps['name']>(''),
  label: makeStringProp<VanFieldProps['label']>(''),
  required: makeCustomProp<VanFieldProps['required']>(Boolean, false),
  disabled: makeCustomProp<VanFieldProps['disabled']>(Boolean, false),
  readonly: makeCustomProp<VanFieldProps['readonly']>(Boolean, false),
  columns: makeCustomProp<VanPickerProps['columns']>(Array, []),
  title: String,
  type: makeStringProp<PickerTypeEnum>(PickerTypeEnum.normal),
  modelValue: makeArrayProp<number | string | undefined>(),
  autosize: makeCustomProp<VanFieldProps['autosize']>(Boolean, false),
  columnsFieldNames: makeCustomProp<VanPickerProps['columnsFieldNames']>(Object, {}),
};

export type PickerValueType = {
  selectedValues: VanPickerProps['modelValue'];
  selectedOptions: (VanPickerOption | undefined)[];
};

export type FieldPickerProps = ExtractPropTypes<typeof fieldPickerProps>;

/**
 * ["440306000000", "440306001000", "440306001008"]
 * @param areaCodeArr
 * @param columns
 * @param valueKey
 */
function findAreaObjByCode(areaCodeArr: string[], columns: AreaCodeNode[], valueKey = 'value') {
  let curInd = 0;
  let curLevel = 0;
  let curLevelNodes: Nullable<AreaCodeNode[]> = columns;
  let curNode: Nullable<AreaCodeNode> = curLevelNodes?.[curInd];
  const areaCodeObjArr: AreaCodeNode[] = [];
  while (curLevelNodes && curLevel < areaCodeArr.length && curInd < curLevelNodes.length) {
    curInd += 1;
    if (curNode?.[valueKey] === areaCodeArr[curLevel]) {
      curLevelNodes = curNode.children;
      curInd = 0;
      curLevel += 1;
      areaCodeObjArr.push(curNode);
    }
    curNode = curLevelNodes?.[curInd] as AreaCodeNode;
  }
  return areaCodeObjArr;
}

export default defineComponent({
  name,
  props: fieldPickerProps,
  emits: ['pickerConfirm', 'update:modelValue'],
  setup(props, { emit }) {
    const showPicker = ref(false);
    const pickerDefaultVal = ref(props.modelValue);
    // 地址选择器显示值
    const addrVal = ref('');

    if (props.type === PickerTypeEnum.address && props.modelValue.length) {
      const addrObjArr = findAreaObjByCode(
        props.modelValue as string[],
        props.columns as AreaCodeNode[],
        props?.columnsFieldNames?.value,
      );
      if (addrObjArr.length === props.modelValue.length) {
        addrVal.value = addrObjArr[props.modelValue.length - 1].path;
      }
    }

    // 没有默认日期时，设定当前日期值为默认选定值
    if (props.type === PickerTypeEnum.date && !Object.keys(pickerDefaultVal.value).length) {
      const date = new Date();
      pickerDefaultVal.value = [
        `${date.getFullYear()}`,
        `${date.getMonth() + 1}`.padStart(2, '0'),
        `${date.getDate()}`.padStart(2, '0'),
      ];
    }

    const triggerPicker = () => {
      if (props.disabled || props.readonly) return;
      // 关闭弹层时，默认值重置：当前picker状态值还是true
      if (showPicker.value) pickerDefaultVal.value = props.modelValue;
      return (showPicker.value = !showPicker.value);
    };
    const onConfirm = (value) => {
      showPicker.value = false;
      emit('pickerConfirm', value);
      switch (props.type) {
        case PickerTypeEnum.address:
          emit('update:modelValue', [
            value.selectedOptions[0]?.code,
            value.selectedOptions[1]?.code,
            value.selectedOptions[2]?.code,
          ]);
          // emit('update:modelValue', value.selectedOptions);
          addrVal.value = (value.selectedOptions?.[2] as AreaCodeNode)?.path;
          break;
        case PickerTypeEnum.date:
          emit('update:modelValue', [
            value.selectedOptions[0]?.value,
            value.selectedOptions[1]?.value,
            value.selectedOptions[2]?.value,
          ]);
          break;
        case PickerTypeEnum.normal:
        default:
          emit('update:modelValue', [value.selectedOptions[0]?.value]);
          break;
      }
    };
    const onCancel = () => (showPicker.value = false);

    // 只处理 date 和 normal 两种格式的显示值
    const fieldVal = computed((): VanPickerOption => {
      if (props.type === PickerTypeEnum.date) {
        return { text: props.modelValue.join('-') };
      }
      if (props.type === PickerTypeEnum.normal) {
        return (
          props.columns.filter((i: VanPickerOption) => {
            return props.modelValue[0] === i.value;
          })[0] ?? ''
        );
      }
      return { text: '' };
    });

    // 普通类型：单列选择
    const renderNormal = () => {
      return (
        <>
          <Field
            id={props.name}
            v-model={fieldVal.value.text}
            name={props.name}
            label={props.label}
            is-link
            readonly
            autosize
            required={props.required}
            placeholder="请选择"
            input-align="left"
            disabled={props.disabled}
            onClick={triggerPicker}
          />
          <Popup show={showPicker.value} onClickOverlay={onCancel} position="bottom">
            <Picker
              v-model={pickerDefaultVal.value}
              title={props.title}
              option-height={t(40)}
              columns={props.columns}
              columnsFieldNames={props.columnsFieldNames}
              onConfirm={onConfirm}
              onCancel={onCancel}
            />
          </Popup>
        </>
      );
    };

    // 日期类型
    const renderDate = () => {
      const minDate = new Date(1920, 0, 1);
      const maxDate = new Date(2050, 0, 1);
      return (
        <>
          <Field
            id={props.name}
            v-model={fieldVal.value.text}
            name={props.name}
            label={props.label}
            is-link
            readonly
            autosize
            placeholder="请选择"
            input-align="left"
            required={props.required}
            disabled={props.disabled}
            onClick={triggerPicker}
          />
          <Popup show={showPicker.value} onClickOverlay={onCancel} position="bottom">
            <DatePicker
              v-model={pickerDefaultVal.value}
              title={props.title}
              option-height={t(40)}
              min-date={minDate}
              max-date={maxDate}
              onConfirm={onConfirm}
              onCancel={onCancel}
            />
          </Popup>
        </>
      );
    };

    // 普通类型：地区选择
    const renderAddress = () => {
      return (
        <>
          <Field
            id={props.name}
            v-model={addrVal.value}
            name={props.name}
            label={props.label}
            is-link
            readonly
            autosize
            placeholder="请选择"
            input-align="left"
            required={props.required}
            disabled={props.disabled}
            onClick={triggerPicker}
          />
          <Popup show={showPicker.value} onClickOverlay={onCancel} position="bottom">
            <Picker
              v-model={pickerDefaultVal.value}
              title={props.title}
              option-height={t(40)}
              columns={props.columns}
              columnsFieldNames={props.columnsFieldNames}
              onConfirm={onConfirm}
              onCancel={onCancel}
            />
          </Popup>
        </>
      );
    };

    return () => {
      switch (props.type) {
        case PickerTypeEnum.date:
          return renderDate();
        case PickerTypeEnum.address:
          return renderAddress();
        case PickerTypeEnum.normal:
        default:
          return renderNormal();
      }
    };
  },
});
